/** Live invalidation over `/v1/events`.
 *
 *  The TUI deliberately skipped this and reloads explicitly. A phone client sits
 *  open all day, so it needs to notice a change made from the CLI or the TUI.
 *
 *  **Why this is not `EventSource`.** The native API cannot set request headers,
 *  and the journal API authenticates by `Authorization: Bearer` only — there is
 *  no query-parameter token, and putting one in a URL is ruled out by R10/D9a
 *  because URLs land in logs and history. So the stream is read with `fetch` and
 *  the frames are parsed here. The cost is reimplementing reconnection; the
 *  benefit is that reconnection becomes ours to control, which the parameters
 *  below need anyway.
 *
 *  **What the stream actually covers.** The server records a change for
 *  `entry`, `reflection`, `day_context` and `day` — and nothing else. Habits,
 *  the reading log, the gratitude log, commonplace books and settings do not
 *  emit events, so a change to those made by another client will not arrive
 *  here. Documented rather than papered over: the affected screens keep the
 *  refetch-on-return behaviour they already had.
 */

import type { Session } from '../api/client';

/** One recorded change, normalised from an SSE frame. */
export interface Invalidation {
  cursor: number;
  /** The SSE event name, which is the server's change `kind`. */
  kind: string;
  /** `entry` | `reflection` | `day_context` | `day` today. Left open because the
   *  server may add more and an unknown type must not be dropped silently. */
  resourceType: string;
  resourceId: string | null;
  entryId: string;
  dates: string[];
  months: string[];
}

/** What the user is currently looking at, so a change elsewhere costs nothing. */
export type Surface =
  | { kind: 'day'; date: string }
  | { kind: 'week'; from: string; to: string }
  | { kind: 'month'; month: string };

export type StreamState =
  /** Opening, or reopening after a drop. */
  | 'connecting'
  /** Connected and receiving. */
  | 'live'
  /** Dropped, waiting out a backoff. */
  | 'retrying'
  /** Given up. The caller falls back to refetching when the app is next shown,
   *  which is the TUI's consistency model and a correct degraded state. */
  | 'degraded';

/* ---------------------------------------------------------------- parameters
 *
 * D-6, resolved here.
 *
 * The server's own numbers are the fixed points: it sends a keep-alive comment
 * every 15s (`KeepAlive::interval` in `src/lib.rs`) and admits at most 16
 * concurrent streams per process (`EVENT_STREAM_CONNECTIONS`). Both matter more
 * for a phone than for the short-lived CLI request the hosted plan's `U7`
 * fairness policy was sized against, and 16 is small enough that a client which
 * leaks streams across reloads would exhaust the pool on its own.
 *
 * The contract the gateway has to honour for these to work, stated so `U7` can
 * be written against it: pass keep-alive bytes through unbuffered, preserve
 * `Last-Event-ID` on the way in, and return 429 rather than hanging when a
 * fairness limit is hit. Everything below is a client-side choice that assumes
 * only those three.
 */

/** No bytes for this long means the connection is dead even though the socket is
 *  open — the common mobile case, where a network changes under a live request
 *  and nothing is delivered or errored. Two missed keep-alives plus slack. */
export const STALL_MS = 40_000;

/** Reconnect backoff: exponential from 1s, capped at 30s, full jitter so a
 *  device that drops and returns repeatedly does not beat on the server in
 *  lockstep with itself. */
export const BACKOFF_BASE_MS = 1_000;
export const BACKOFF_CAP_MS = 30_000;

/** A 429 means someone holds the 16 permits, not that we did anything wrong, so
 *  it waits out the cap immediately instead of climbing to it. */
export const LIMIT_BACKOFF_MS = BACKOFF_CAP_MS;

/** Hidden for this long and the stream is released, because a backgrounded tab
 *  holding one of 16 permits is holding it for nobody. Not immediate: flicking
 *  to another app and back should not cost a reconnect. */
export const HIDDEN_RELEASE_MS = 10_000;

/** Consecutive failures before dropping to `degraded`. At the 30s cap this is a
 *  little over three minutes of trying. */
export const MAX_ATTEMPTS = 8;

/* -------------------------------------------------------------- pure helpers */

export interface Frame {
  id: string | null;
  event: string | null;
  data: string;
  /** Keep-alives arrive as comments. They carry nothing but prove liveness. */
  comment: boolean;
}

/** Split a buffer into complete SSE frames, returning the incomplete remainder.
 *
 *  Chunk boundaries fall wherever the network puts them, so a frame routinely
 *  arrives in pieces and the leftover has to survive to the next read.
 */
export function parseFrames(buffer: string): { frames: Frame[]; rest: string } {
  const frames: Frame[] = [];
  // Normalise CRLF first: the spec allows \r\n, \n and \r as line terminators.
  const normalised = buffer.replace(/\r\n|\r/g, '\n');
  const parts = normalised.split('\n\n');
  // The last part is either an incomplete frame or an empty string.
  const rest = parts.pop() ?? '';
  for (const part of parts) {
    if (!part) continue;
    let id: string | null = null;
    let event: string | null = null;
    const data: string[] = [];
    let comment = false;
    for (const line of part.split('\n')) {
      if (line.startsWith(':')) {
        comment = true;
        continue;
      }
      const colon = line.indexOf(':');
      const field = colon === -1 ? line : line.slice(0, colon);
      // Exactly one optional leading space is stripped, per the spec.
      let value = colon === -1 ? '' : line.slice(colon + 1);
      if (value.startsWith(' ')) value = value.slice(1);
      if (field === 'id') id = value;
      else if (field === 'event') event = value;
      else if (field === 'data') data.push(value);
    }
    frames.push({ id, event, data: data.join('\n'), comment });
  }
  return { frames, rest };
}

/** Turn a data frame into an `Invalidation`, or null if it is not one.
 *
 *  A frame that cannot be understood is dropped rather than thrown: one
 *  malformed event must not take down a stream that is otherwise fine.
 */
export function toInvalidation(frame: Frame): Invalidation | null {
  if (frame.comment || !frame.data) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(frame.data);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const body = parsed as Record<string, unknown>;
  const cursor = Number(body.cursor ?? frame.id);
  if (!Number.isFinite(cursor)) return null;
  const list = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
  return {
    cursor,
    kind: frame.event ?? String(body.kind ?? 'unknown'),
    resourceType: typeof body.resource_type === 'string' ? body.resource_type : 'unknown',
    resourceId: typeof body.resource_id === 'string' ? body.resource_id : null,
    entryId: typeof body.entry_id === 'string' ? body.entry_id : '',
    dates: list(body.invalidated_dates),
    months: list(body.invalidated_months),
  };
}

/** Full jitter over an exponential backoff. `jitter` is a 0..1 sample, passed in
 *  rather than drawn here so the schedule is testable. */
export function backoffDelay(attempt: number, jitter: number): number {
  const ceiling = Math.min(BACKOFF_CAP_MS, BACKOFF_BASE_MS * 2 ** Math.max(0, attempt));
  return Math.round(ceiling * Math.min(1, Math.max(0, jitter)));
}

/** Whether a change touches what is on screen.
 *
 *  The point of the cursor and the invalidated-date lists is that most changes
 *  cost nothing: editing a task in March should not refetch today.
 */
export function affects(invalidation: Invalidation, surface: Surface): boolean {
  const monthOf = (date: string) => date.slice(0, 7);
  switch (surface.kind) {
    case 'day':
      return invalidation.dates.includes(surface.date);
    case 'week':
      return invalidation.dates.some((date) => date >= surface.from && date <= surface.to);
    case 'month':
      // A month face shows both the month inbox and every dated entry in it, so
      // either list can invalidate it.
      return (
        invalidation.months.includes(surface.month) ||
        invalidation.dates.some((date) => monthOf(date) === surface.month)
      );
  }
}

/* ------------------------------------------------------------ the connection */

export interface SubscribeOptions {
  session: Session;
  /** Where to resume from. Take it from a day projection's `event_cursor`, so a
   *  fresh subscription means "from now" — connecting without it replays the
   *  journal's entire change history. */
  from: number;
  onInvalidate: (invalidation: Invalidation) => void;
  onState?: (state: StreamState) => void;
  /** The server rejected our cursor: 409 `event_cursor_invalid`, which happens
   *  when the cursor is ahead of the log — a restored or rotated database. The
   *  caller refetches to learn a fresh cursor and subscribes again. The stream
   *  stops until it does, because reconnecting with the same bad cursor would
   *  loop. */
  onCursorLost?: () => void;
}

/** Hold one stream open, following the parameters above. Returns an unsubscribe.
 *
 *  Exactly one connection exists per call: the abort controller is replaced only
 *  after the previous request is aborted, so a visibility flap cannot leave two
 *  streams — and two streams would consume two of sixteen permits and deliver
 *  every event twice.
 */
export function subscribe(options: SubscribeOptions): () => void {
  const { session, onInvalidate, onState, onCursorLost } = options;
  let cursor = options.from;
  let attempt = 0;
  let stopped = false;
  let controller: AbortController | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let hiddenTimer: ReturnType<typeof setTimeout> | null = null;

  const announce = (state: StreamState) => onState?.(state);

  /** Read through a function: TypeScript otherwise narrows `visibilityState`
   *  from the guard at the top of `run` and decides the check in the catch block
   *  is unreachable — but the whole point is that it changes while we await. */
  const isHidden = () => document.visibilityState === 'hidden';

  const abort = () => {
    controller?.abort();
    controller = null;
  };

  const clearRetry = () => {
    if (retryTimer !== null) clearTimeout(retryTimer);
    retryTimer = null;
  };

  const scheduleRetry = (delay: number) => {
    clearRetry();
    if (stopped) return;
    announce('retrying');
    retryTimer = setTimeout(() => {
      retryTimer = null;
      void run();
    }, delay);
  };

  async function run(): Promise<void> {
    if (stopped || controller || isHidden()) return;
    const own = new AbortController();
    controller = own;
    announce('connecting');

    // Watchdog: the request can stay open with nothing arriving after a network
    // change, which no error and no timeout would otherwise surface.
    let stall: ReturnType<typeof setTimeout> | null = null;
    const resetStall = () => {
      if (stall !== null) clearTimeout(stall);
      stall = setTimeout(() => own.abort(), STALL_MS);
    };

    try {
      const response = await fetch(`${session.baseUrl}/v1/events`, {
        headers: {
          authorization: `Bearer ${session.token}`,
          accept: 'text/event-stream',
          // The server reads the cursor from this header and replays everything
          // after it, which is what makes a reconnect lossless.
          'last-event-id': String(cursor),
        },
        signal: own.signal,
        // Never let a cache or the service worker sit between us and a stream.
        cache: 'no-store',
      });

      if (response.status === 409) {
        // Our cursor is ahead of the log. Retrying cannot fix it.
        abort();
        announce('degraded');
        onCursorLost?.();
        return;
      }
      if (response.status === 429) {
        abort();
        scheduleRetry(LIMIT_BACKOFF_MS);
        return;
      }
      if (!response.ok || !response.body) {
        abort();
        attempt += 1;
        if (attempt >= MAX_ATTEMPTS) return announce('degraded');
        return scheduleRetry(backoffDelay(attempt, Math.random()));
      }

      // Connected. Only now is the attempt counter cleared, so a server that
      // accepts and immediately drops still backs off.
      attempt = 0;
      announce('live');
      resetStall();

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        resetStall();
        buffer += decoder.decode(value, { stream: true });
        const { frames, rest } = parseFrames(buffer);
        buffer = rest;
        for (const frame of frames) {
          if (frame.id) {
            const parsed = Number(frame.id);
            if (Number.isFinite(parsed)) cursor = parsed;
          }
          const invalidation = toInvalidation(frame);
          if (invalidation) onInvalidate(invalidation);
        }
      }
      // A clean end of stream is still a disconnect.
      abort();
      if (!stopped) scheduleRetry(backoffDelay(attempt, Math.random()));
    } catch {
      // Aborts land here too, including our own on unsubscribe and on going
      // hidden — both of which set `stopped` or leave the visibility handler to
      // reconnect, so neither schedules a retry from here.
      abort();
      if (stopped || isHidden()) return;
      attempt += 1;
      if (attempt >= MAX_ATTEMPTS) return announce('degraded');
      scheduleRetry(backoffDelay(attempt, Math.random()));
    } finally {
      if (stall !== null) clearTimeout(stall);
    }
  }

  const onVisibility = () => {
    if (stopped) return;
    if (isHidden()) {
      hiddenTimer = setTimeout(() => {
        hiddenTimer = null;
        abort();
        clearRetry();
      }, HIDDEN_RELEASE_MS);
      return;
    }
    if (hiddenTimer !== null) {
      // Back before the release fired: the stream was never dropped.
      clearTimeout(hiddenTimer);
      hiddenTimer = null;
      return;
    }
    // Returning from a real background. Reconnect at once rather than waiting
    // out a backoff — the user is looking at a stale screen right now, and
    // `last-event-id` makes the gap lossless.
    attempt = 0;
    clearRetry();
    void run();
  };

  document.addEventListener('visibilitychange', onVisibility);
  void run();

  return () => {
    stopped = true;
    document.removeEventListener('visibilitychange', onVisibility);
    if (hiddenTimer !== null) clearTimeout(hiddenTimer);
    clearRetry();
    abort();
  };
}
